import { logger } from "../utils/logger";
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router';
import { useSearchParams } from 'react-router';
import { Play, ArrowRight, Sparkles, Filter, Target, Clock, Star, SlidersHorizontal, Shield, Award, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner@2.0.3';
import { getApiUrl, getApiHeaders } from '../utils/apiConfig';
import { Helmet } from 'react-helmet-async';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';

// Lazy-loaded heavy components (modals + sidebar card)
const VideoPlannerModal = lazy(() => import('../components/VideoPlannerModal').then(m => ({ default: m.VideoPlannerModal })));
const ContactFormModal  = lazy(() => import('../components/ContactFormModal').then(m => ({ default: m.ContactFormModal })));
const VideoModal        = lazy(() => import('../components/VideoModal').then(m => ({ default: m.VideoModal })));
const BundleOfferCard   = lazy(() => import('../components/BundleOfferCard').then(m => ({ default: m.BundleOfferCard })));

// Eagerly-imported small components (used in the critical render path)
import { VideoCategoryBadge } from '../components/VideoCategoryBadge';

const shuffleArray = (array: any[]) => {
  return [...array].sort(() => Math.random() - 0.5);
};

// ============================================
// SKELETON COMPONENTS
// ============================================

const VideoSkeleton = () => {
  return (
    <div className="rounded-2xl overflow-hidden border border-white/60 shadow-lg bg-white/80 backdrop-blur-sm">
      <div className="aspect-video bg-gray-200 relative overflow-hidden">
        <div className="absolute inset-0 shimmer" />
      </div>
      <div className="p-5 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-1/3 relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-5 bg-gray-200 rounded w-3/4 relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-5/6 relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-10 bg-gray-200 rounded-full relative overflow-hidden mt-4">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
    </div>
  );
};

const HeroSkeleton = () => (
  <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
    <div className="max-w-7xl mx-auto">
      <div className="text-center space-y-6">
        <div className="flex justify-center">
          <div className="h-8 w-36 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="space-y-3 max-w-2xl mx-auto">
          <div className="h-8 bg-gray-200 rounded w-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="space-y-2 max-w-xl mx-auto">
          <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-5/6 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {[140, 180, 160].map((w, i) => (
            <div key={i} className="h-9 bg-gray-200 rounded-full relative overflow-hidden" style={{ width: w }}>
              <div className="absolute inset-0 shimmer" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const FilterDrawerSkeleton = () => (
  <div className="w-80 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 shadow-xl p-6 space-y-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gray-200 rounded-xl relative overflow-hidden">
        <div className="absolute inset-0 shimmer" />
      </div>
      <div className="space-y-1.5 flex-1">
        <div className="h-4 bg-gray-200 rounded w-1/2 relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-3 bg-gray-200 rounded w-2/3 relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
    </div>
    <div className="h-10 bg-gray-200 rounded-2xl relative overflow-hidden">
      <div className="absolute inset-0 shimmer" />
    </div>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="h-12 bg-gray-200 rounded-xl relative overflow-hidden">
        <div className="absolute inset-0 shimmer" />
      </div>
    ))}
  </div>
);

const ResultsHeaderSkeleton = () => (
  <div className="mb-6 space-y-2">
    <div className="h-7 bg-gray-200 rounded w-40 relative overflow-hidden">
      <div className="absolute inset-0 shimmer" />
    </div>
    <div className="h-4 bg-gray-200 rounded w-28 relative overflow-hidden">
      <div className="absolute inset-0 shimmer" />
    </div>
  </div>
);

const CtaSectionSkeleton = () => (
  <section className="relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#0a0a1f] overflow-hidden">
    <div className="max-w-4xl mx-auto text-center space-y-6">
      <div className="flex justify-center">
        <div className="h-8 w-44 bg-white/20 rounded-full relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
      <div className="space-y-3 max-w-2xl mx-auto">
        <div className="h-10 bg-white/20 rounded w-full relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-10 bg-white/20 rounded w-4/5 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
      <div className="flex gap-4 justify-center flex-wrap">
        <div className="h-14 w-48 bg-white/20 rounded-full relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-14 w-48 bg-white/20 rounded-full relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
    </div>
  </section>
);

const StatsSkeleton = () => (
  <section className="relative py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
    <div className="max-w-6xl mx-auto space-y-10">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-52 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-72 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {[0, 1, 2].map(i => (
          <div key={i} className="h-36 bg-gray-200 rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================
// TYPESCRIPT INTERFACES
// ============================================

interface VideoExample {
  id: string;
  title: string;
  outcome: string;
  description: string;
  industry: string;
  industries: string[];
  purpose: string;
  purposes: string[];
  videoUrl: string;
  videoProvider: 'youtube' | 'vimeo' | 'wistia';
  videoId?: string;
  thumbnail?: string;
  duration?: string;
  status: 'published' | 'draft';
  featured?: boolean;
  keywords: string[];
  createdAt: string;
  updatedAt: string;
}

interface CategoryItem {
  name: string;
  slug: string;
  count: number;
}

interface UICategory {
  title: string;
  subcategories: string[];
  count?: number;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

function extractVideoId(url: string, provider: string): string | null {
  if (!url) return null;
  try {
    if (provider === 'youtube') {
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
        /^([a-zA-Z0-9_-]{11})$/
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
    } else if (provider === 'vimeo') {
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match) return match[1];
    } else if (provider === 'wistia') {
      const match = url.match(/wistia\.com\/medias\/([a-zA-Z0-9]+)/);
      if (match) return match[1];
    }
  } catch (error) {
    logger.error('Error extracting video ID:', error);
  }
  return null;
}

function generateThumbnail(videoUrl: string, provider: string, videoId?: string): string {
  const id = videoId || extractVideoId(videoUrl, provider);
  if (!id) return '';
  if (provider === 'youtube') {
    return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
  } else if (provider === 'vimeo') {
    return `https://vumbnail.com/${id}.jpg`;
  } else if (provider === 'wistia') {
    return `https://embed-ssl.wistia.com/deliveries/${id}/file.jpg`;
  }
  return '';
}

export function VideoExamplesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') as 'industry' | 'purpose') || 'industry';
  const initialCategory = searchParams.get('category') || null;

  const [activeTab, setActiveTab] = useState<'industry' | 'purpose'>(initialTab);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategory);

  const [allVideos, setAllVideos] = useState<VideoExample[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<VideoExample[]>([]);
  const [industryCategories, setIndustryCategories] = useState<UICategory[]>([]);
  const [purposeCategories, setPurposeCategories] = useState<UICategory[]>([]);

  const [loading, setLoading] = useState(true);
  const [ctaLoading, setCtaLoading] = useState(true);
  const [trustedLoading, setTrustedLoading] = useState(true);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [ctaSection, setCtaSection] = useState<any>(null);
  const [trustedClientsSection, setTrustedClientsSection] = useState<any>(null);

  const [isPlannerModalOpen, setIsPlannerModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const [selectedVideo, setSelectedVideo] = useState<VideoExample | null>(null);
  const [totalVideosCount, setTotalVideosCount] = useState(0);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (activeTab !== 'industry') params.tab = activeTab;
    if (selectedCategory) params.category = selectedCategory;
    setSearchParams(params, { replace: true });
  }, [activeTab, selectedCategory]);

  useEffect(() => {
    if (!initialCategory || allVideos.length === 0) return;
    if (initialTab === 'industry') {
      setFilteredVideos(allVideos.filter(v => v.industries.includes(initialCategory)));
    } else {
      setFilteredVideos(allVideos.filter(v => v.purposes.includes(initialCategory)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allVideos]);

  useEffect(() => {
    fetchVideosAndCategories();
  }, []);

  const fetchVideosAndCategories = async () => {
    try {
      setLoading(true);

      const [videosRes, catsRes] = await Promise.all([
        fetch(`${getApiUrl()}/video-examples`, { headers: getApiHeaders() }),
        fetch(`${getApiUrl()}/video-examples/categories`, { headers: getApiHeaders() }),
      ]);

      if (!videosRes.ok) throw new Error(`Failed to fetch videos: ${videosRes.status}`);
      if (!catsRes.ok) throw new Error(`Failed to fetch categories: ${catsRes.status}`);

      const [videosData, catsData] = await Promise.all([videosRes.json(), catsRes.json()]);

      const transformedVideos: VideoExample[] = (videosData.videos || videosData.data || []).map((v: any) => {
        const thumbnail = v.thumbnail?.trim()
          ? v.thumbnail
          : generateThumbnail(v.videoUrl, v.videoProvider, v.videoId);
        return {
          ...v,
          thumbnail,
          industry: v.industries?.[0] || v.industry || 'Uncategorized',
          purpose: v.purposes?.[0] || v.purpose || 'General',
          industries: v.industries || (v.industry ? [v.industry] : []),
          purposes: v.purposes || (v.purpose ? [v.purpose] : []),
          keywords: v.keywords || [],
        };
      });

      const categoriesData = catsData.data || catsData;
      const transformedIndustries: UICategory[] = (categoriesData.industries || []).map((cat: CategoryItem) => ({
        title: cat.name,
        subcategories: [],
        count: cat.count,
      }));
      const transformedPurposes: UICategory[] = (categoriesData.purposes || []).map((cat: CategoryItem) => ({
        title: cat.name,
        subcategories: [],
        count: cat.count,
      }));

      const stableVideos = shuffleArray(transformedVideos);

      setAllVideos(stableVideos);
      setFilteredVideos(stableVideos);
      setIndustryCategories(transformedIndustries);
      setPurposeCategories(transformedPurposes);
      setTotalVideosCount(categoriesData.totalVideos || transformedVideos.length);
    } catch (error) {
      logger.error('❌ Error fetching video examples:', error);
      toast.error('Failed to load video examples.');
      setAllVideos([]);
      setFilteredVideos([]);
      setIndustryCategories([]);
      setPurposeCategories([]);
    } finally {
      setLoading(false);
    }

    // CMS sections fetched in parallel — don't block the main grid
    Promise.allSettled([
      fetch(`${getApiUrl()}/video-examples/sections/cta`, { headers: getApiHeaders() }),
      fetch(`${getApiUrl()}/video-examples/sections/trusted-clients`, { headers: getApiHeaders() }),
    ]).then(async ([ctaResult, trustedResult]) => {
      if (ctaResult.status === 'fulfilled' && ctaResult.value.ok) {
        const data = await ctaResult.value.json();
        setCtaSection(data.data);
      }
      setCtaLoading(false);

      if (trustedResult.status === 'fulfilled' && trustedResult.value.ok) {
        const data = await trustedResult.value.json();
        setTrustedClientsSection(data.data);
      }
      setTrustedLoading(false);
    });
  };

  const handleCategoryClick = useCallback((category: string) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
      setFilteredVideos(allVideos);
    } else {
      setSelectedCategory(category);
      if (activeTab === 'industry') {
        setFilteredVideos(allVideos.filter(v => v.industries.includes(category)));
      } else {
        setFilteredVideos(allVideos.filter(v => v.purposes.includes(category)));
      }
    }
  }, [selectedCategory, activeTab, allVideos]);

  const handleTabChange = useCallback((tab: 'industry' | 'purpose') => {
    setActiveTab(tab);
    setSelectedCategory(null);
    setFilteredVideos(allVideos);
  }, [allVideos]);

  const handleClearFilters = useCallback(() => {
    setSelectedCategory(null);
    setFilteredVideos(allVideos);
  }, [allVideos]);

  const handlePlayVideo = useCallback((video: VideoExample) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  }, []);

  const handleGetPricing = useCallback((video: VideoExample) => {
    setSelectedVideo(video);
    setIsContactModalOpen(true);
  }, []);

  const displayVideos = selectedCategory ? filteredVideos : allVideos;

  return (
    <>
      <Helmet>
        <title>Video Examples | 3D Animation & Explainer Videos | Creabiz Studio</title>
        <meta name="description" content="Browse 50+ animation examples across 8+ industries. Filter by industry and use case to find relevant 3D animation and explainer video examples for your business." />
        <meta property="og:title" content="Video Examples | 3D Animation & Explainer Videos | Creabiz Studio" />
        <meta property="og:description" content="Browse 50+ animation examples across 8+ industries. Filter by industry and use case to find relevant examples for your business." />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Video Examples | Creabiz Studio" />
        <meta property="twitter:description" content="Browse 50+ animation examples across 8+ industries." />
      </Helmet>

      {/* Hero Section */}
      {loading ? (
        <HeroSkeleton />
      ) : (
        <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#2F2E5C] via-[#4a487a] to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D74444] via-[#e85e5e] to-transparent rounded-full blur-3xl" />
          </div>
          <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/40 shadow-lg">
                <Sparkles className="w-4 h-4 text-[#E87D3E]" />
                <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">Video Examples</span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 leading-tight text-[#2F2E5C]">
                Find Relevant Examples in Under 2 Minutes
              </h1>
              <p className="text-base text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                Filter by your industry and specific use case to see what's possible. Every example shows outcome achieved, industry application, and video type.
              </p>
              <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap">
                {[
                  { icon: Target, text: `${totalVideosCount}+ Examples Across ${industryCategories.length} Industries` },
                  { icon: Filter, text: `${industryCategories.length * purposeCategories.length}+ Keywords + Use Case Combinations` },
                  { icon: Clock, text: 'Find Your Example in Under 2 Minutes' }
                ].map((item, i) => (
                  <div key={i} className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2.5 rounded-full border border-white/60 shadow-md hover:shadow-lg transition-all group">
                    <item.icon className="w-4 h-4 text-[#D74444] group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="pt-8 pb-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-[1800px] mx-auto">
          <div className="mb-6">
            {loading ? (
              <div className="h-12 w-40 bg-gray-200 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            ) : (
              <button
                onClick={() => setIsFilterDrawerOpen(!isFilterDrawerOpen)}
                aria-expanded={isFilterDrawerOpen}
                aria-controls="filter-drawer"
                className="group bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white rounded-full px-6 py-3 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center gap-3"
              >
                <div className="relative">
                  <SlidersHorizontal className="w-5 h-5" />
                  {selectedCategory && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#D74444] rounded-full border-2 border-white" />
                  )}
                </div>
                <span className="font-bold">{isFilterDrawerOpen ? 'Hide Filters' : 'Show Filters'}</span>
              </button>
            )}
          </div>

          <div className="relative flex gap-6">
            <aside
              id="filter-drawer"
              aria-label="Video filters"
              className={`flex-shrink-0 transition-all duration-300 ease-out ${
                isFilterDrawerOpen ? 'w-80 opacity-100' : 'w-0 opacity-0'
              }`}
            >
              {loading ? (
                isFilterDrawerOpen ? <FilterDrawerSkeleton /> : null
              ) : (
                <div className={`premium-scrollbar w-80 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 shadow-xl sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto transition-all duration-300 ${
                  isFilterDrawerOpen ? 'translate-x-0 p-6' : '-translate-x-full p-0'
                }`}>
                  <div className="mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-xl flex items-center justify-center shadow-lg">
                        <Filter className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-[#2F2E5C]">Filters</h3>
                        <p className="text-xs text-gray-500">Refine your search</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="text-xs font-semibold text-[#2F2E5C]/60 uppercase tracking-wider mb-3 px-1">Browse By</div>
                    <div role="tablist" className="flex gap-2 p-1.5 bg-gray-50/80 backdrop-blur-sm rounded-2xl border border-gray-100">
                      {(['industry', 'purpose'] as const).map(tab => (
                        <button
                          key={tab}
                          role="tab"
                          aria-selected={activeTab === tab}
                          onClick={() => handleTabChange(tab)}
                          className={`relative flex-1 px-4 py-2.5 rounded-xl transition-all duration-300 font-semibold text-sm overflow-hidden ${
                            activeTab === tab ? 'text-white shadow-lg' : 'text-gray-600 hover:text-[#2F2E5C]'
                          }`}
                        >
                          {activeTab === tab && (
                            <>
                              <div className="absolute inset-0 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D]" />
                              <div className="absolute inset-0 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] opacity-50 blur-xl" />
                            </>
                          )}
                          <span className="relative capitalize">{tab}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-[#2F2E5C]/60 uppercase tracking-wider mb-3 px-1">
                      {activeTab === 'industry' ? 'Industries' : 'Purposes'}
                    </div>
                    <div className="space-y-2" role="listbox" aria-label={activeTab === 'industry' ? 'Industry filters' : 'Purpose filters'}>
                      {(activeTab === 'industry' ? industryCategories : purposeCategories).map((category, i) => (
                        <button
                          key={category.title}
                          role="option"
                          aria-selected={selectedCategory === category.title}
                          onClick={() => handleCategoryClick(category.title)}
                          className={`group w-full text-left px-4 py-3.5 rounded-xl transition-all border relative overflow-hidden ${
                            selectedCategory === category.title
                              ? 'bg-gradient-to-br from-[#E87D3E]/15 via-[#E8B84D]/10 to-transparent border-[#E87D3E]/40 shadow-lg shadow-[#E87D3E]/20'
                              : 'bg-white/80 backdrop-blur-sm border-white/60 text-gray-700 hover:border-[#E87D3E]/30 hover:bg-white hover:shadow-md'
                          }`}
                        >
                          {selectedCategory === category.title && (
                            <>
                              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-[#E8B84D]/30 via-[#E87D3E]/20 to-transparent rounded-bl-full" />
                              <div className="absolute bottom-0 left-0 w-2 h-full bg-gradient-to-t from-[#E87D3E] to-[#E8B84D] rounded-l-xl" />
                            </>
                          )}
                          <div className="relative flex items-center justify-between">
                            <div className={`font-semibold text-sm ${selectedCategory === category.title ? 'text-[#E87D3E]' : ''}`}>
                              {category.title}
                            </div>
                            {category.count !== undefined && (
                              <span className="text-xs opacity-60 ml-2">({category.count})</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedCategory && (
                    <div className="mt-6">
                      <button
                        onClick={handleClearFilters}
                        className="group relative w-full text-center px-4 py-3 rounded-xl text-sm font-semibold bg-white/80 backdrop-blur-sm border border-[#E87D3E]/30 text-[#E87D3E] hover:bg-gradient-to-r hover:from-[#E87D3E] hover:to-[#E8B84D] hover:text-white hover:border-transparent shadow-md hover:shadow-lg hover:shadow-[#E87D3E]/30 transition-all duration-300 overflow-hidden"
                      >
                        Clear Filter
                      </button>
                    </div>
                  )}

                  <div className="mt-8">
                    <Suspense fallback={
                      <div className="h-40 bg-gray-200 rounded-2xl relative overflow-hidden">
                        <div className="absolute inset-0 shimmer" />
                      </div>
                    }>
                      <BundleOfferCard variant="sidebar" />
                    </Suspense>
                  </div>
                </div>
              )}
            </aside>

            <main className="flex-1 min-w-0">
              {loading ? (
                <ResultsHeaderSkeleton />
              ) : (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-[#2F2E5C] mb-2">
                    {selectedCategory || 'All Videos'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    Showing {displayVideos.length} video{displayVideos.length !== 1 ? 's' : ''}
                    {selectedCategory && ` in ${selectedCategory}`}
                  </p>
                </div>
              )}

              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => <VideoSkeleton key={i} />)
                  : displayVideos.map((video, index) => {
                      const gradients = [
                        "from-[#E87D3E] to-[#E8B84D]",
                        "from-[#D74444] to-[#e85e5e]",
                        "from-[#E8B84D] to-[#E87D3E]"
                      ];
                      const gradient = gradients[index % gradients.length];

                      return (
                        <div
                          key={video.id}
                          className="group relative bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/60 hover:border-white/80 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                        >
                          <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                          <div
                            className="relative aspect-video bg-gradient-to-br from-[#2F2E5C] via-[#4a487a] to-[#2F2E5C] flex items-center justify-center overflow-hidden cursor-pointer"
                            onClick={() => handlePlayVideo(video)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Play ${video.title}`}
                            onKeyDown={(e) => e.key === 'Enter' && handlePlayVideo(video)}
                          >
                            {video.thumbnail && (
                              <img
                                src={video.thumbnail}
                                alt={video.title}
                                loading="lazy"
                                decoding="async"
                                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-20 transition-all duration-300`} />
                            <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px'}} />
                            <div className="relative z-10">
                              <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 blur-xl transition-all duration-300`} />
                              <Button className="relative bg-white hover:bg-white text-[#2F2E5C] rounded-full w-14 h-14 shadow-xl group-hover:scale-110 transition-all duration-300">
                                <Play className="w-6 h-6 ml-0.5" />
                              </Button>
                            </div>
                            {video.duration && (
                              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                {video.duration}
                              </div>
                            )}
                          </div>

                          <div className="relative p-5">
                            <div className="flex gap-2 mb-3 flex-wrap">
                              <VideoCategoryBadge
                                primary={video.industry}
                                additional={video.industries.slice(1)}
                                type="industry"
                              />
                              <VideoCategoryBadge
                                primary={video.purpose}
                                additional={video.purposes.slice(1)}
                                type="purpose"
                              />
                            </div>
                            <div className="flex items-start gap-2 mb-2">
                              <Target className="w-4 h-4 text-[#D74444] mt-0.5 flex-shrink-0" />
                              <div className="text-sm font-bold text-[#D74444]">{video.outcome}</div>
                            </div>
                            <h3 className="font-bold text-[#2F2E5C] mb-2 group-hover:bg-gradient-to-r group-hover:from-[#2F2E5C] group-hover:to-[#4a487a] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                              {video.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-2">
                              {video.description}
                            </p>
                            <Button
                              onClick={() => handleGetPricing(video)}
                              className={`w-full bg-gradient-to-r ${gradient} hover:opacity-90 text-white rounded-full h-10 gap-2 text-sm shadow-md hover:shadow-lg transition-all group-hover:gap-3`}
                            >
                              Get Your Custom Quote
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
              </div>

              {!loading && displayVideos.length === 0 && (
                <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl border-2 border-dashed border-gray-200">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Play className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No examples in this combination yet</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">Most projects are custom requests outside standard categories.</p>
                  <div className="flex gap-3 justify-center flex-wrap">
                    <Link to="/contact">
                      <Button className="bg-[#D74444] hover:bg-[#c13939] text-white rounded-full px-6 h-11 gap-2">
                        Tell Us What You Need
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button onClick={handleClearFilters} variant="outline" className="rounded-full px-6 h-11">
                      View All Examples
                    </Button>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {ctaLoading ? (
        <CtaSectionSkeleton />
      ) : ctaSection && ctaSection.enabled && (
        <section className="relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#0a0a1f] text-white overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#D74444] via-[#e85e5e] to-[#ff6b6b] opacity-15 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}} />
            <div className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-gradient-to-tr from-[#E8B84D] via-[#E87D3E] to-[#ffa94d] opacity-15 rounded-full blur-3xl" />
          </div>
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/30 shadow-lg">
              <Sparkles className="w-4 h-4 text-[#E8B84D]" />
              <span className="text-sm font-semibold bg-gradient-to-r from-[#E8B84D] to-[#E87D3E] bg-clip-text text-transparent">Ready to Get Started?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">{ctaSection.headline}</h2>
            <p className="text-lg sm:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">{ctaSection.description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={ctaSection.primaryButtonLink}>
                <Button className="group relative overflow-hidden bg-gradient-to-r from-[#D74444] via-[#e85e5e] to-[#c13939] hover:from-[#c13939] hover:via-[#D74444] hover:to-[#e85e5e] text-white rounded-full px-8 h-14 gap-3 shadow-2xl shadow-[#D74444]/30 hover:scale-105 transition-all duration-300 font-bold text-lg">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative z-10 flex items-center gap-3">
                    {ctaSection.primaryButtonText}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              </Link>
              <Link to={ctaSection.secondaryButtonLink}>
                <Button className="group bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/50 rounded-full px-8 h-14 gap-3 shadow-lg hover:shadow-xl transition-all duration-300 font-bold text-lg">
                  <span className="flex items-center gap-3">
                    {ctaSection.secondaryButtonText}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              </Link>
            </div>
            <div className="mt-12 pt-8 border-t border-white/20">
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/70">
                {ctaSection.trustIndicators.map((indicator: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    {idx === 0 && <Shield className="w-4 h-4 text-[#E8B84D]" />}
                    {idx === 1 && <Clock className="w-4 h-4 text-[#E8B84D]" />}
                    {idx === 2 && <Award className="w-4 h-4 text-[#E8B84D]" />}
                    <span>{indicator}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CMS Stats — kept from trusted clients API, testimonial replaced with global component */}
      {trustedLoading ? (
        <StatsSkeleton />
      ) : trustedClientsSection && trustedClientsSection.enabled && (
        <section className="relative py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-[#E8B84D] via-[#E87D3E] to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-tr from-[#2F2E5C] via-[#4A4878] to-transparent rounded-full blur-3xl" />
          </div>
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
                <Shield className="w-4 h-4 text-[#E87D3E]" />
                <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">Trusted by Industry Leaders</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">{trustedClientsSection.sectionHeading}</h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">{trustedClientsSection.description}</p>
            </div>

            {/* CMS-driven stats cards */}
            <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {trustedClientsSection.stats.map((stat: any, i: number) => {
                const gradients = ['from-[#E87D3E] to-[#E8B84D]', 'from-[#D74444] to-[#e85e5e]', 'from-[#E8B84D] to-[#E87D3E]'];
                const icons = [Award, Star, Zap];
                const StatIcon = icons[i % icons.length];
                const gradient = gradients[i % gradients.length];
                return (
                  <div key={i} className="group relative text-center p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity`} />
                    <div className="relative z-10">
                      <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center bg-gradient-to-br ${gradient} shadow-lg`}>
                        <StatIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-[#2F2E5C] mb-1">{stat.value}</div>
                      <div className="text-sm text-gray-600 font-semibold">{stat.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Global Testimonials Section */}
      <TestimonialsSection />

      {/* Modals */}
      <Suspense fallback={null}>
        <VideoPlannerModal isOpen={isPlannerModalOpen} onClose={() => setIsPlannerModalOpen(false)} />
      </Suspense>
      <Suspense fallback={null}>
        <ContactFormModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          videoTitle={selectedVideo?.title}
          videoIndustry={selectedVideo?.industry}
          videoPurpose={selectedVideo?.purpose}
          videoUrl={selectedVideo?.videoUrl}
        />
      </Suspense>
      <Suspense fallback={null}>
        <VideoModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          videoUrl={selectedVideo?.videoUrl}
          videoProvider={selectedVideo?.videoProvider}
          videoId={selectedVideo?.videoId}
          title={selectedVideo?.title}
        />
      </Suspense>
    </>
  );
}